import type {
  JSONContent,
  MarkdownParseHelpers,
  MarkdownRendererHelpers,
  MarkdownToken,
  RenderContext,
} from '@tiptap/core';
import { isBlankLineMarker, retainEscapesAsText } from './paragraph-markdown';

/**
 * Parses a `list_item` token, wrapping every text block in its own paragraph.
 *
 * marked lexes item bodies with `state.top` off, so an item's blocks arrive as
 * `text` tokens (never `paragraph`), separated by a `space` token where the
 * source held a blank line. The stock `ListItem.parseMarkdown` wraps only the
 * first `text` token in a paragraph; later ones fall through the fallback
 * handler as bare inline text nodes — schema-invalid under
 * `paragraph block*`, rendered in HTML without a `<p>` wrap, and joined back
 * onto the first paragraph on serialize. Wrapping each `text` token keeps a
 * loose item's paragraphs real paragraphs. Nested blocks (lists, code,
 * quotes) still parse via their own handlers.
 *
 * Bullet and ordered items both route here (`ListItem.parseMarkdown`). The
 * render half dispatches by node type, so it covers both list kinds too.
 */
export const parseListItemMarkdown = (
  token: MarkdownToken,
  helpers: MarkdownParseHelpers
): JSONContent => {
  const content: JSONContent[] = [];
  for (const child of token.tokens ?? []) {
    if (child.type === 'text') {
      // Same escape retention as `parseParagraphMarkdown`: without it
      // @tiptap/markdown drops marked's `escape` token, so an item holding
      // literal `1\. part water` loses the dot on load.
      const inline = helpers.parseInline(retainEscapesAsText(child.tokens ?? []));
      content.push({
        type: 'paragraph',
        // Item bodies skip parseParagraphMarkdown; invert the blank-line marker here.
        content: isBlankLineMarker(inline) ? [] : inline,
      });
    } else if (child.type !== 'space') {
      content.push(...helpers.parseChildren([child]));
    }
  }
  // `content: 'paragraph block*'` requires a leading paragraph.
  if (content.length === 0) content.push({ type: 'paragraph', content: [] });
  return { type: 'listItem', content };
};

// Same marker choice as the stock renderer: `- ` under a bullet list, a
// dot-delimited number continuing the list's `start` under an ordered list.
const listItemPrefix = (ctx: RenderContext): string => {
  if (ctx.parentType === 'orderedList') {
    const rawStart = (ctx.meta?.parentAttrs as { start?: unknown } | undefined)?.start;
    const start = typeof rawStart === 'number' && rawStart ? rawStart : 1;
    return `${start + ctx.index}. `;
  }
  return '- ';
};

/**
 * Serializes a list item, separating sibling paragraphs with a blank line.
 *
 * The stock renderer (`renderNestedMarkdownContent`) joins an item's blocks
 * with a single `\n`, so a second paragraph re-lexes as a lazy continuation
 * of the first — the blank line is lost on the first pass and the line
 * dedents out of the list on the next. A blank line before each later
 * paragraph makes the loose item its own fixed point. Non-paragraph blocks
 * (nested lists, code) keep the stock single-`\n` join.
 *
 * A later paragraph indents by the marker width, not `helpers.indent`'s fixed
 * two spaces: after a blank line, a paragraph must reach the item's content
 * column to stay in the item (two spaces under `1. ` re-lexes it out of the
 * list). Nested lists keep the stock two-space indent, which marked accepts.
 */
export const renderListItemMarkdown = (
  node: JSONContent,
  helpers: MarkdownRendererHelpers,
  ctx: RenderContext
): string => {
  const prefix = listItemPrefix(ctx);
  const [first, ...rest] = node.content ?? [];
  let output = `${prefix}${first ? helpers.renderChildren([first]) : ''}`;
  for (const child of rest) {
    const rendered = helpers.renderChildren([child]);
    if (!rendered) continue;
    const isParagraph = child.type === 'paragraph';
    const indent = (line: string): string =>
      isParagraph ? ' '.repeat(prefix.length) + line : helpers.indent(line);
    const indented = rendered
      .split('\n')
      .map((line) => (line ? indent(line) : ''))
      .join('\n');
    output += (isParagraph ? '\n\n' : '\n') + indented;
  }
  return output;
};

export const listItemMarkdownSpec = {
  parseMarkdown: parseListItemMarkdown,
  renderMarkdown: renderListItemMarkdown,
};
